import { LayoutElement, DxfPolylineMeta, ToolboxBomLine, ToolboxItem } from '../types';
import { CLASS_LABELS } from '../constants';
import {
  ALL_RAIL_TYPES,
  CROSS_RAIL_TYPE,
  getCurveRadius,
  getCurveSweepAngle,
  getFixedCurveLengthMeters,
  getRailEndpoints,
  isConnectorRailType,
  isCurveRailType,
  isJunctionRailType,
  isStraightLikeRailType,
  resolveRailType,
  type RailShapeType,
} from './topology';
import { isLayoutEquipmentElement } from './layout3d';
import {
  shouldUseDxfCalibration,
  worldUnitsToEffectiveMeters,
  worldUnitsToLayoutMeters,
} from './measurement';
import {
  DEFAULT_BOM_PRICE_BOOK,
  deriveInfrastructureLines,
  formatBomCurrency,
  parseMoney,
  readCatalogProcurement,
  resolveBomProcurement,
  type BomCostSource,
  type BomCurrency,
  type BomProcurementOverride,
  type LayoutBomPriceBook,
} from './bomPricing';

export const DEFAULT_RAIL_STOCK_LENGTH_METERS = 2;

const PREFERRED_STRAIGHT_TRACK_TOOL_ID = 'rail-at1';

export type CatalogBomSource = Pick<ToolboxItem, 'id' | 'type'> &
  Partial<
    Pick<ToolboxItem, 'sku' | 'name' | 'category' | 'defaultProperties' | 'bom' | 'customFields'>
  >;

export type CatalogStockLengthSource = Pick<CatalogBomSource, 'id' | 'type' | 'defaultProperties'>;

function readPositiveMeters(value: unknown): number | undefined {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

/** Catalog `lengthMeters` for a straight-track tool, used as BOM stock section length. */
export function catalogToolStockLengthMeters(item: CatalogStockLengthSource): number | undefined {
  const railType = item.defaultProperties?.railType;
  if (railType != null && railType !== 'straight') return undefined;
  if (item.type !== 'overhead-rail') return undefined;
  return readPositiveMeters(item.defaultProperties?.lengthMeters);
}

/**
 * Stock section length from the toolbox catalog (API / tool config).
 * Prefers the standard AT1 straight track; otherwise the first straight-rail tool.
 */
export function resolveCatalogRailStockLengthMeters(
  items: readonly CatalogStockLengthSource[] | undefined
): number | undefined {
  if (!items?.length) return undefined;
  const candidates = items.filter((item) => catalogToolStockLengthMeters(item) != null);
  if (candidates.length === 0) return undefined;
  const preferred =
    candidates.find((item) => item.id === PREFERRED_STRAIGHT_TRACK_TOOL_ID) ?? candidates[0];
  return catalogToolStockLengthMeters(preferred);
}

export type LayoutBomCategory =
  | 'Track'
  | 'Stocker'
  | 'Equipment'
  | 'OHTs'
  | 'Infrastructure'
  | 'Other';

export type LayoutBomFilterCategory =
  | 'All'
  | 'Tracks'
  | 'Stockers'
  | 'Equipment'
  | 'OHTs'
  | 'Infrastructure'
  | 'Other';

export type LayoutBomTrackDetails = {
  layoutObjectCount: number;
  totalCenterlineMeters: number;
  standardSectionMeters: number;
  requiredSections: number;
};

export type LayoutBomInstallationItem = {
  id: string;
  name: string;
  category: string;
  description: string;
  qtyPerUnit: number;
  totalQty: number;
  unit: string;
};

export type LayoutBomRow = {
  id: string;
  itemNo: number;
  component: string;
  category: LayoutBomCategory;
  specification: string;
  quantity: number;
  unit: string;
  totalLengthMeters: number | null;
  source: 'layout' | 'derived';
  manufacturer: string;
  partNumber: string;
  /** Formatted unit cost, or an em dash when the row is unpriced. */
  unitCost: string;
  leadTime: string;
  /** Numeric unit cost in the BOM currency; `null` when unpriced. */
  unitCostValue?: number | null;
  /** `unitCostValue × quantity`; `null` when unpriced. */
  extendedCostValue?: number | null;
  costSource?: BomCostSource;
  /** For derived infrastructure rows: the rule the quantity came from. */
  derivedFrom?: string;
  trackDetails?: LayoutBomTrackDetails;
  installationComponents?: LayoutBomInstallationItem[];
};

export type LayoutBomSummary = {
  totalComponents: number;
  trackLengthMeters: number;
  trackSections: number;
  stockers: number;
  equipment: number;
  ohts: number;
  junctions: number;
  /** Sum of every priced row's extended cost. Unpriced rows contribute nothing. */
  estimatedTotalCost: number;
  currency: BomCurrency;
  pricedRows: number;
  unpricedRows: number;
  costByCategory: Partial<Record<LayoutBomCategory, number>>;
};

export type LayoutBomCalibrationInput = {
  floorplanBounds?: boolean;
  floorplanPolylineCount?: number;
  scaleFactor?: number;
  dxfUnitsPerMeter?: number | null;
  millimetersPerCanvasUnit?: number | null;
  canvasUnitsPerMeter?: number | null;
};

export type LayoutBomTrackDebugEntry = {
  id: string;
  trackType: string;
  start: { x: number; y: number };
  end: { x: number; y: number };
  worldLength: number;
  physicalLengthMeters: number;
  metersPerWorldUnit: number;
  usedLengthMetersProperty: boolean;
};

export type LayoutBomResult = {
  rows: LayoutBomRow[];
  summary: LayoutBomSummary;
  stockLengthMeters: number;
  /** Present in development builds to validate per-segment geometry. */
  trackDebug?: LayoutBomTrackDebugEntry[];
};

/** Placeholder until `pushRow` resolves procurement from the price book. */
const EMPTY_PROCUREMENT = {
  manufacturer: '—',
  partNumber: '—',
  unitCost: '—',
  leadTime: '—',
} as const;

/** Per-element procurement overrides, read from `customProperties`. */
function elementProcurementOverride(el: LayoutElement): BomProcurementOverride | undefined {
  const partNumber = stringProp(el.customProperties?.partNumber);
  const unitCost = parseMoney(el.customProperties?.unitCost);
  if (!partNumber && unitCost == null) return undefined;
  return {
    ...(partNumber ? { partNumber } : {}),
    ...(unitCost != null ? { unitCost } : {}),
  };
}

const TRACK_LABELS: Record<string, string> = {
  straight: 'Overhead Straight Track',
  connector: 'H-Connector',
  curve45: '45° Curved Track',
  curve90: '90° Curved Track',
  curve180: '180° Curved Track',
  'junction-circle': 'Circular Junction',
  'cross-rail': 'Cross Rail Junction',
  branch: 'Branch Track',
  lift: 'Lift Track',
};

const stringProp = (value: unknown, fallback = ''): string =>
  typeof value === 'string' && value.trim() ? value.trim() : fallback;

export function formatBomLengthMeters(meters: number | null): string {
  if (meters === null || !Number.isFinite(meters)) return '—';
  if (meters <= 0) return '—';
  return `${Number(meters.toFixed(2))} m`;
}

function formatExportLengthMeters(meters: number | null): string {
  if (meters === null || !Number.isFinite(meters) || meters <= 0) return '—';
  return `${meters.toFixed(2)} m`;
}

export type BomExportMainRow = {
  name: string;
  category: string;
  description: string;
  qty: number;
  unit: string;
  totalLength: string;
  partNumber: string;
  unitCost: string;
  extendedCost: string;
};

export type BomExportInstallationRow = {
  mainComponent: string;
  name: string;
  category: string;
  description: string;
  qtyPerMain: number;
  totalQty: number;
  unit: string;
};

function exportMainName(row: LayoutBomRow): string {
  if (row.id === 'amhs-oht') return 'OHT';
  return row.component;
}

function exportMainCategory(row: LayoutBomRow): string {
  if (row.id === 'amhs-oht') return 'OHT';
  return row.category;
}

function exportMainDescription(row: LayoutBomRow): string {
  if (row.id === 'track-straight') {
    const section = row.trackDetails?.standardSectionMeters;
    if (section && section > 0) return `${section} m straight track section`;
    return row.specification.replace(/section$/i, 'straight track section');
  }
  if (row.id === 'track-curve45') return '45° curved track section';
  if (row.id === 'track-curve90') return '90° curved track section';
  if (row.id === 'track-curve180') return '180° curved track section';
  if (row.id === 'amhs-oht') return 'Overhead hoist transport vehicle';
  if (row.derivedFrom) return `${row.specification} (${row.derivedFrom})`;
  return row.specification;
}

/** Export currency: rows carry formatted strings, so read the currency off the summary when given. */
const EXPORT_CURRENCY: BomCurrency = 'USD';

export function buildLayoutBomExportSheets(
  rows: LayoutBomRow[],
  currency: BomCurrency = EXPORT_CURRENCY
): {
  main: BomExportMainRow[];
  installation: BomExportInstallationRow[];
  estimatedTotal: string;
} {
  const total = rows.reduce((sum, row) => sum + (row.extendedCostValue ?? 0), 0);
  return {
    main: rows.map((row) => ({
      name: exportMainName(row),
      category: exportMainCategory(row),
      description: exportMainDescription(row),
      qty: row.quantity,
      unit: row.unit,
      totalLength: formatExportLengthMeters(row.totalLengthMeters),
      partNumber: row.partNumber,
      unitCost: formatBomCurrency(row.unitCostValue ?? null, currency),
      extendedCost: formatBomCurrency(row.extendedCostValue ?? null, currency),
    })),
    estimatedTotal: formatBomCurrency(total, currency),
    installation: rows.flatMap((row) => {
      const mainComponent = exportMainName(row);
      return (row.installationComponents ?? []).map((item) => ({
        mainComponent,
        name: item.name,
        category: item.category || '—',
        description: item.description || '—',
        qtyPerMain: item.qtyPerUnit,
        totalQty: item.totalQty,
        unit: item.unit,
      }));
    }),
  };
}

function csvEscape(value: string | number): string {
  const text = String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function csvTable(headers: string[], lines: Array<Array<string | number>>): string {
  return [headers, ...lines].map((line) => line.map(csvEscape).join(',')).join('\n');
}

const MAIN_SHEET_HEADERS = [
  'Name',
  'Category',
  'Description',
  'Qty',
  'Unit',
  'Total Length',
  'Part Number',
  'Unit Cost',
  'Ext. Cost',
];

function mainSheetLines(
  main: BomExportMainRow[],
  estimatedTotal: string
): Array<Array<string | number>> {
  const lines: Array<Array<string | number>> = main.map((row) => [
    row.name,
    row.category,
    row.description,
    row.qty,
    row.unit,
    row.totalLength,
    row.partNumber,
    row.unitCost,
    row.extendedCost,
  ]);
  lines.push([
    'Estimated total',
    '',
    'Priced rows only; unpriced rows excluded',
    '',
    '',
    '',
    '',
    '',
    estimatedTotal,
  ]);
  return lines;
}

/** Two labeled CSV sections (Sheet 1 / Sheet 2). */
export function layoutBomToCsv(rows: LayoutBomRow[]): string {
  const { main, installation, estimatedTotal } = buildLayoutBomExportSheets(rows);
  const sheet1 = csvTable(MAIN_SHEET_HEADERS, mainSheetLines(main, estimatedTotal));
  const sheet2 = csvTable(
    ['Main Component', 'Name', 'Category', 'Description', 'Qty / Main', 'Total Qty', 'Unit'],
    installation.map((row) => [
      row.mainComponent,
      row.name,
      row.category,
      row.description,
      row.qtyPerMain,
      row.totalQty,
      row.unit,
    ])
  );
  return [
    'Sheet 1 — Main Components',
    sheet1,
    '',
    'Sheet 2 — Installation Components',
    sheet2,
  ].join('\n');
}

function xmlEscape(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function spreadsheetCell(value: string | number): string {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return `<Cell><Data ss:Type="Number">${value}</Data></Cell>`;
  }
  return `<Cell><Data ss:Type="String">${xmlEscape(String(value))}</Data></Cell>`;
}

function spreadsheetSheet(
  name: string,
  headers: string[],
  lines: Array<Array<string | number>>
): string {
  const rows = [headers, ...lines]
    .map((line) => `<Row>${line.map(spreadsheetCell).join('')}</Row>`)
    .join('');
  return `<Worksheet ss:Name="${xmlEscape(name)}"><Table>${rows}</Table></Worksheet>`;
}

/** Excel-compatible workbook with Main Components and Installation Components sheets. */
export function layoutBomToSpreadsheetXml(rows: LayoutBomRow[]): string {
  const { main, installation, estimatedTotal } = buildLayoutBomExportSheets(rows);
  const sheet1 = spreadsheetSheet(
    'Main Components',
    MAIN_SHEET_HEADERS,
    mainSheetLines(main, estimatedTotal)
  );
  const sheet2 = spreadsheetSheet(
    'Installation Components',
    ['Main Component', 'Name', 'Category', 'Description', 'Qty / Main', 'Total Qty', 'Unit'],
    installation.map((row) => [
      row.mainComponent,
      row.name,
      row.category,
      row.description,
      row.qtyPerMain,
      row.totalQty,
      row.unit,
    ])
  );
  return (
    `<?xml version="1.0"?>\n` +
    `<?mso-application progid="Excel.Sheet"?>\n` +
    `<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" ` +
    `xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">` +
    `${sheet1}${sheet2}` +
    `</Workbook>`
  );
}

export function layoutBomCategoryFilter(
  row: LayoutBomRow,
  filter: LayoutBomFilterCategory
): boolean {
  if (filter === 'All') return true;
  if (filter === 'Tracks') return row.category === 'Track';
  if (filter === 'Stockers') return row.category === 'Stocker';
  if (filter === 'Equipment') return row.category === 'Equipment';
  if (filter === 'OHTs') return row.category === 'OHTs';
  if (filter === 'Infrastructure') return row.category === 'Infrastructure';
  return row.category === 'Other';
}

export function layoutBomMatchesSearch(row: LayoutBomRow, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const nested = (row.installationComponents ?? []).flatMap((item) => [
    item.name,
    item.category,
    item.description,
  ]);
  const haystack = [
    row.component,
    row.category,
    row.specification,
    row.partNumber,
    row.manufacturer,
    row.source,
    ...nested,
  ]
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
}

function stockerSpecification(el: LayoutElement): string {
  const sku = stringProp(el.customProperties?.sku);
  if (sku) return sku;
  const process = stringProp(el.customProperties?.processType);
  if (process) return process;
  if (el.customProperties?.outsideStocker === true) return 'Outside Stocker';
  return 'Standard Stocker';
}

function equipmentSpecification(el: LayoutElement): string {
  const equipmentType = stringProp(el.customProperties?.equipmentType);
  if (equipmentType) return equipmentType;
  const processType = stringProp(el.customProperties?.processType);
  if (processType) return processType;
  const sku = stringProp(el.customProperties?.sku);
  if (sku) return sku;
  if (el.type !== 'rectangle') return CLASS_LABELS[el.type] ?? el.type;
  return 'General Equipment';
}

function otherSpecification(el: LayoutElement): string {
  const sku = stringProp(el.customProperties?.sku);
  if (sku) return sku;
  return CLASS_LABELS[el.type] ?? el.type;
}

function catalogRailType(item: CatalogBomSource): string {
  return (
    stringProp(item.defaultProperties?.railType) ||
    (item.type === CROSS_RAIL_TYPE ? CROSS_RAIL_TYPE : '')
  );
}

function findCatalogTool(
  items: readonly CatalogBomSource[],
  predicate: (item: CatalogBomSource) => boolean
): CatalogBomSource | undefined {
  const matches = items.filter(predicate);
  return matches.find((item) => (item.bom?.length ?? 0) > 0) ?? matches[0];
}

function catalogToolForBomRow(
  rowId: string,
  specification: string,
  items: readonly CatalogBomSource[]
): CatalogBomSource | undefined {
  if (rowId === 'track-straight') {
    return (
      findCatalogTool(items, (item) => item.id === PREFERRED_STRAIGHT_TRACK_TOOL_ID) ??
      findCatalogTool(items, (item) => catalogRailType(item) === 'straight')
    );
  }
  if (rowId.startsWith('track-')) {
    const railType = rowId.slice('track-'.length);
    return findCatalogTool(items, (item) => catalogRailType(item) === railType);
  }
  if (rowId === 'amhs-oht') {
    return findCatalogTool(items, (item) => item.type === 'oht-vehicle');
  }
  if (rowId.startsWith('stocker-')) {
    return findCatalogTool(items, (item) => {
      if (item.type !== 'stocker') return false;
      const sku = stringProp(item.sku) || stringProp(item.defaultProperties?.sku);
      const process = stringProp(item.defaultProperties?.processType);
      const spec =
        sku ||
        process ||
        (item.defaultProperties?.outsideStocker === true ? 'Outside Stocker' : 'Standard Stocker');
      return spec === specification;
    });
  }
  if (rowId.startsWith('equipment-')) {
    return findCatalogTool(items, (item) => {
      if (item.type === 'stocker' || item.type === 'oht-vehicle') return false;
      const spec =
        stringProp(item.defaultProperties?.equipmentType) ||
        stringProp(item.defaultProperties?.processType) ||
        stringProp(item.sku) ||
        (item.type !== 'rectangle' ? (CLASS_LABELS[item.type] ?? item.type) : 'General Equipment');
      return spec === specification;
    });
  }
  if (rowId.startsWith('other-')) {
    return findCatalogTool(items, (item) => {
      const spec = stringProp(item.sku) || CLASS_LABELS[item.type] || item.type;
      return spec === specification;
    });
  }
  return undefined;
}

export function installationComponentsFromCatalog(
  tool: CatalogBomSource | undefined,
  parentQuantity: number
): LayoutBomInstallationItem[] {
  if (!tool?.bom?.length) return [];
  return tool.bom
    .filter((row) => stringProp(row.name))
    .map((row: ToolboxBomLine) => {
      const qtyPerUnit = Number.isFinite(row.qty) && row.qty > 0 ? row.qty : 0;
      return {
        id: row.id,
        name: row.name.trim(),
        category: stringProp(row.category),
        description: stringProp(row.description),
        qtyPerUnit,
        totalQty: qtyPerUnit * parentQuantity,
        unit: row.unit || 'pcs',
      };
    });
}

/**
 * Aggregated layout BOM from canonical Redux layout elements (not Three.js / simulation).
 */
export function resolveLayoutBomMetersPerWorldUnit(calibration: LayoutBomCalibrationInput): number {
  const hasFloorPlanGeometry = shouldUseDxfCalibration(
    Boolean(calibration.floorplanBounds),
    calibration.floorplanPolylineCount ?? 0
  );
  if (
    hasFloorPlanGeometry &&
    Number.isFinite(calibration.scaleFactor) &&
    Number.isFinite(calibration.dxfUnitsPerMeter) &&
    (calibration.scaleFactor as number) > 0 &&
    (calibration.dxfUnitsPerMeter as number) > 0
  ) {
    return (calibration.scaleFactor as number) / (calibration.dxfUnitsPerMeter as number);
  }
  if (
    Number.isFinite(calibration.millimetersPerCanvasUnit) &&
    (calibration.millimetersPerCanvasUnit as number) > 0
  ) {
    return (calibration.millimetersPerCanvasUnit as number) / 1000;
  }
  if (
    Number.isFinite(calibration.canvasUnitsPerMeter) &&
    (calibration.canvasUnitsPerMeter as number) > 0
  ) {
    return 1 / (calibration.canvasUnitsPerMeter as number);
  }
  return worldUnitsToLayoutMeters(1);
}

export function buildLayoutBom(params: {
  elements: Record<string, LayoutElement>;
  stockLengthMeters?: number;
  /** Toolbox catalog tools — `lengthMeters` on straight track becomes stock length. */
  toolboxItems?: readonly CatalogBomSource[];
  calibration?: LayoutBomCalibrationInput;
  /** Part numbers, unit costs and infrastructure rules. Defaults to the built-in book. */
  priceBook?: LayoutBomPriceBook;
  /** Add hangers, power rail and controllers derived from the drawn track. Default true. */
  includeInfrastructure?: boolean;
  /** @deprecated Prefer `calibration` — kept for callers passing scale only. */
  scaleFactor?: number;
  /** @deprecated Prefer `calibration` */
  dxfUnitsPerMeter?: number;
}): LayoutBomResult {
  const calibration: LayoutBomCalibrationInput = {
    ...params.calibration,
    scaleFactor: params.calibration?.scaleFactor ?? params.scaleFactor,
    dxfUnitsPerMeter: params.calibration?.dxfUnitsPerMeter ?? params.dxfUnitsPerMeter,
  };
  const priceBook = params.priceBook ?? DEFAULT_BOM_PRICE_BOOK;
  const includeInfrastructure = params.includeInfrastructure ?? true;
  /** First procurement override seen for each aggregated row id. */
  const elementOverrides = new Map<string, BomProcurementOverride>();
  const rememberOverride = (rowId: string, el: LayoutElement) => {
    if (elementOverrides.has(rowId)) return;
    const override = elementProcurementOverride(el);
    if (override) elementOverrides.set(rowId, override);
  };
  let explicitSuspensionRods = 0;

  const stockLengthMeters =
    readPositiveMeters(params.stockLengthMeters) ??
    resolveCatalogRailStockLengthMeters(params.toolboxItems) ??
    DEFAULT_RAIL_STOCK_LENGTH_METERS;

  let straightLengthMeters = 0;
  let straightObjectCount = 0;
  const trackDebug: LayoutBomTrackDebugEntry[] = [];
  const curveLengthMeters: Partial<Record<RailShapeType, number>> = {};
  const curveCounts: Partial<Record<RailShapeType, number>> = {};
  let connectorCount = 0;
  let circularJunctionCount = 0;
  let crossRailCount = 0;
  const branchLiftCounts: Partial<Record<'branch' | 'lift', number>> = {};

  const stockerCounts = new Map<string, number>();
  const equipmentCounts = new Map<string, number>();
  const otherCounts = new Map<string, number>();
  let ohtCount = 0;

  for (const element of Object.values(params.elements)) {
    if (!element.metadata.visible) continue;

    if (element.type === 'oht-vehicle') {
      ohtCount += 1;
      rememberOverride('amhs-oht', element);
      continue;
    }

    if (element.type === 'stocker') {
      const key = stockerSpecification(element);
      stockerCounts.set(key, (stockerCounts.get(key) ?? 0) + 1);
      rememberOverride(`stocker-${key}`, element);
      continue;
    }

    if (isLayoutEquipmentElement(element)) {
      const key = equipmentSpecification(element);
      equipmentCounts.set(key, (equipmentCounts.get(key) ?? 0) + 1);
      rememberOverride(`equipment-${key}`, element);
      continue;
    }

    if (element.type === CROSS_RAIL_TYPE) {
      const railType = resolveRailType(element);
      if (isJunctionRailType(railType)) {
        circularJunctionCount += 1;
      } else {
        crossRailCount += 1;
      }
      continue;
    }

    if (!ALL_RAIL_TYPES.has(element.type)) {
      if (
        element.type === 'support-pillar' ||
        element.type === 'horizontal-beam' ||
        element.type === 'suspension-rod'
      ) {
        const key = otherSpecification(element);
        otherCounts.set(key, (otherCounts.get(key) ?? 0) + 1);
        rememberOverride(`other-${key}`, element);
        if (element.type === 'suspension-rod') explicitSuspensionRods += 1;
      }
      continue;
    }

    const railType = resolveRailType(element);

    if (isJunctionRailType(railType)) {
      circularJunctionCount += 1;
      continue;
    }

    if (isCurveRailType(railType)) {
      curveCounts[railType] = (curveCounts[railType] ?? 0) + 1;
      const arcMeters = layoutCurveTrackLengthMeters(element, calibration);
      curveLengthMeters[railType] = (curveLengthMeters[railType] ?? 0) + arcMeters;
      continue;
    }

    if (isConnectorRailType(railType)) {
      connectorCount += 1;
      continue;
    }

    if (railType === 'branch' || railType === 'lift') {
      branchLiftCounts[railType] = (branchLiftCounts[railType] ?? 0) + 1;
      continue;
    }

    if (isStraightLikeRailType(railType)) {
      const segment = layoutStraightTrackLengthBreakdown(element, calibration);
      if (segment.meters > 0) {
        straightLengthMeters += segment.meters;
        straightObjectCount += 1;
        if (import.meta.env.DEV) {
          trackDebug.push({
            id: element.id,
            trackType: 'straight',
            start: segment.start,
            end: segment.end,
            worldLength: segment.worldLength,
            physicalLengthMeters: segment.meters,
            metersPerWorldUnit: segment.metersPerWorldUnit,
            usedLengthMetersProperty: segment.usedStoredLength,
          });
        }
      }
    }
  }

  const catalogItems = params.toolboxItems ?? [];
  const rows: LayoutBomRow[] = [];
  let itemNo = 0;
  const pushRow = (partial: Omit<LayoutBomRow, 'itemNo' | 'installationComponents'>) => {
    itemNo += 1;
    const catalogTool =
      partial.source === 'derived'
        ? undefined
        : catalogToolForBomRow(partial.id, partial.specification, catalogItems);
    const installationComponents = installationComponentsFromCatalog(catalogTool, partial.quantity);
    const procurement = resolveBomProcurement({
      rowId: partial.id,
      specification: partial.specification,
      priceBook,
      element: elementOverrides.get(partial.id),
      catalog: catalogTool
        ? { ...readCatalogProcurement(catalogTool.customFields), sku: catalogTool.sku }
        : undefined,
    });
    const extendedCostValue =
      procurement.unitCostValue == null ? null : procurement.unitCostValue * partial.quantity;
    rows.push({
      ...partial,
      component: stringProp(catalogTool?.name) || partial.component,
      itemNo,
      manufacturer: procurement.manufacturer,
      partNumber: procurement.partNumber,
      leadTime: procurement.leadTime,
      unitCost: formatBomCurrency(procurement.unitCostValue, priceBook.currency),
      unitCostValue: procurement.unitCostValue,
      extendedCostValue,
      costSource: procurement.costSource,
      ...(installationComponents.length > 0 ? { installationComponents } : {}),
    });
  };

  const straightSections =
    straightLengthMeters > 0 ? railCountForSegment(straightLengthMeters, stockLengthMeters) : 0;

  if (straightSections > 0) {
    pushRow({
      id: 'track-straight',
      component: TRACK_LABELS.straight,
      category: 'Track',
      specification: `${stockLengthMeters} m section`,
      quantity: straightSections,
      unit: 'pcs',
      totalLengthMeters: straightLengthMeters,
      source: 'layout',
      ...EMPTY_PROCUREMENT,
      trackDetails: {
        layoutObjectCount: straightObjectCount,
        totalCenterlineMeters: straightLengthMeters,
        standardSectionMeters: stockLengthMeters,
        requiredSections: straightSections,
      },
    });
  }

  for (const railType of ['curve45', 'curve90', 'curve180'] as const) {
    const count = curveCounts[railType] ?? 0;
    if (count <= 0) continue;
    const arcTotal = curveLengthMeters[railType] ?? 0;
    pushRow({
      id: `track-${railType}`,
      component: TRACK_LABELS[railType],
      category: 'Track',
      specification: `Curve ${getCurveSweepAngle(railType)}°`,
      quantity: count,
      unit: 'pcs',
      totalLengthMeters: arcTotal > 0 ? arcTotal : null,
      source: 'layout',
      ...EMPTY_PROCUREMENT,
    });
  }

  if (connectorCount > 0) {
    pushRow({
      id: 'track-connector',
      component: TRACK_LABELS.connector,
      category: 'Track',
      specification: 'Parallel track connector',
      quantity: connectorCount,
      unit: 'pcs',
      totalLengthMeters: null,
      source: 'layout',
      ...EMPTY_PROCUREMENT,
    });
  }

  if (circularJunctionCount > 0) {
    pushRow({
      id: 'track-junction-circle',
      component: TRACK_LABELS['junction-circle'],
      category: 'Track',
      specification: 'Standard junction',
      quantity: circularJunctionCount,
      unit: 'pcs',
      totalLengthMeters: null,
      source: 'layout',
      ...EMPTY_PROCUREMENT,
    });
  }

  if (crossRailCount > 0) {
    pushRow({
      id: 'track-cross-rail',
      component: TRACK_LABELS['cross-rail'],
      category: 'Track',
      specification: 'Crossover junction',
      quantity: crossRailCount,
      unit: 'pcs',
      totalLengthMeters: null,
      source: 'layout',
      ...EMPTY_PROCUREMENT,
    });
  }

  for (const railType of ['branch', 'lift'] as const) {
    const count = branchLiftCounts[railType] ?? 0;
    if (count <= 0) continue;
    pushRow({
      id: `track-${railType}`,
      component: TRACK_LABELS[railType],
      category: 'Track',
      specification: TRACK_LABELS[railType],
      quantity: count,
      unit: 'pcs',
      totalLengthMeters: null,
      source: 'layout',
      ...EMPTY_PROCUREMENT,
    });
  }

  for (const [spec, qty] of [...stockerCounts.entries()].sort(([a], [b]) => a.localeCompare(b))) {
    pushRow({
      id: `stocker-${spec}`,
      component: 'Stocker',
      category: 'Stocker',
      specification: spec,
      quantity: qty,
      unit: 'pcs',
      totalLengthMeters: null,
      source: 'layout',
      ...EMPTY_PROCUREMENT,
    });
  }

  for (const [spec, qty] of [...equipmentCounts.entries()].sort(([a], [b]) => a.localeCompare(b))) {
    pushRow({
      id: `equipment-${spec}`,
      component: 'Equipment',
      category: 'Equipment',
      specification: spec,
      quantity: qty,
      unit: 'pcs',
      totalLengthMeters: null,
      source: 'layout',
      ...EMPTY_PROCUREMENT,
    });
  }

  if (ohtCount > 0) {
    pushRow({
      id: 'amhs-oht',
      component: 'OHT Vehicle',
      category: 'OHTs',
      specification: 'Layout OHT',
      quantity: ohtCount,
      unit: 'pcs',
      totalLengthMeters: null,
      source: 'layout',
      ...EMPTY_PROCUREMENT,
    });
  }

  for (const [spec, qty] of [...otherCounts.entries()].sort(([a], [b]) => a.localeCompare(b))) {
    pushRow({
      id: `other-${spec}`,
      component: spec,
      category: 'Other',
      specification: spec,
      quantity: qty,
      unit: 'pcs',
      totalLengthMeters: null,
      source: 'layout',
      ...EMPTY_PROCUREMENT,
    });
  }

  const trackLengthMeters =
    straightLengthMeters +
    (['curve45', 'curve90', 'curve180'] as const).reduce(
      (sum, rt) => sum + (curveLengthMeters[rt] ?? 0),
      0
    );

  const stockers = [...stockerCounts.values()].reduce((a, b) => a + b, 0);
  const curveCount = (['curve45', 'curve90', 'curve180'] as const).reduce(
    (s, rt) => s + (curveCounts[rt] ?? 0),
    0
  );

  if (includeInfrastructure) {
    for (const line of deriveInfrastructureLines(
      {
        trackLengthMeters,
        curveCount,
        junctionCount: circularJunctionCount + crossRailCount,
        ohtCount,
        stockerCount: stockers,
        explicitSuspensionRods,
      },
      priceBook.infrastructure
    )) {
      pushRow({
        id: line.id,
        component: line.component,
        category: 'Infrastructure',
        specification: line.specification,
        quantity: line.quantity,
        unit: line.unit,
        totalLengthMeters: line.totalLengthMeters,
        source: 'derived',
        derivedFrom: line.derivedFrom,
        ...EMPTY_PROCUREMENT,
      });
    }
  }

  const trackSections =
    straightSections +
    connectorCount +
    circularJunctionCount +
    crossRailCount +
    (['curve45', 'curve90', 'curve180'] as const).reduce((s, rt) => s + (curveCounts[rt] ?? 0), 0) +
    (branchLiftCounts.branch ?? 0) +
    (branchLiftCounts.lift ?? 0);

  const equipment = [...equipmentCounts.values()].reduce((a, b) => a + b, 0);

  const costByCategory: Partial<Record<LayoutBomCategory, number>> = {};
  let estimatedTotalCost = 0;
  let pricedRows = 0;
  let unpricedRows = 0;
  for (const row of rows) {
    if (row.extendedCostValue == null) {
      unpricedRows += 1;
      continue;
    }
    pricedRows += 1;
    estimatedTotalCost += row.extendedCostValue;
    costByCategory[row.category] = (costByCategory[row.category] ?? 0) + row.extendedCostValue;
  }

  const summary: LayoutBomSummary = {
    totalComponents: rows.reduce((sum, row) => sum + row.quantity, 0),
    trackLengthMeters,
    trackSections,
    stockers,
    equipment,
    ohts: ohtCount,
    junctions: circularJunctionCount + crossRailCount,
    estimatedTotalCost,
    currency: priceBook.currency,
    pricedRows,
    unpricedRows,
    costByCategory,
  };

  return {
    rows,
    summary,
    stockLengthMeters,
    ...(import.meta.env.DEV && trackDebug.length > 0 ? { trackDebug } : {}),
  };
}

export type StraightTrackLengthBreakdown = {
  meters: number;
  worldLength: number;
  metersPerWorldUnit: number;
  usedStoredLength: boolean;
  start: { x: number; y: number };
  end: { x: number; y: number };
};

/** Physical straight-rail length from stored meters or world centerline endpoints. */
export function layoutStraightTrackLengthBreakdown(
  element: LayoutElement,
  calibration: LayoutBomCalibrationInput
): StraightTrackLengthBreakdown {
  const metersPerWorldUnit = resolveLayoutBomMetersPerWorldUnit(calibration);
  const ends = getRailEndpoints(element);
  const start = ends[0] ?? { x: element.position.x, y: element.position.y };
  const end = ends[1] ?? start;
  const worldLength =
    ends.length >= 2
      ? Math.hypot(end.x - start.x, end.y - start.y)
      : Math.max(element.dimensions.width, 0);

  const stored = Number(element.customProperties?.lengthMeters);
  if (Number.isFinite(stored) && stored > 0) {
    return {
      meters: stored,
      worldLength,
      metersPerWorldUnit,
      usedStoredLength: true,
      start,
      end,
    };
  }

  return {
    meters: worldLength * metersPerWorldUnit,
    worldLength,
    metersPerWorldUnit,
    usedStoredLength: false,
    start,
    end,
  };
}

export function layoutCurveTrackLengthMeters(
  element: LayoutElement,
  calibration: LayoutBomCalibrationInput
): number {
  const stored = Number(element.customProperties?.lengthMeters);
  if (Number.isFinite(stored) && stored > 0) return stored;

  const railType = resolveRailType(element);
  const fixed = getFixedCurveLengthMeters(railType);
  if (fixed && fixed > 0) return fixed;

  const worldArc = layoutTrackWorldLength(element);
  if (!(worldArc > 0)) return 0;
  return worldArc * resolveLayoutBomMetersPerWorldUnit(calibration);
}

const LINE_LIKE_DXF_TYPES = new Set([
  'LINE',
  'LWPOLYLINE',
  'POLYLINE',
  'ARC',
  'CIRCLE',
  'ELLIPSE',
  'SPLINE',
]);

export type RailLengthContribution = {
  source: 'layout' | 'floorplan';
  type: string;
  lengthMeters: number;
  railsNeeded: number;
};

export type RailBomSummary = {
  stockLengthMeters: number;
  totalRails: number;
  totalLengthMeters: number;
  contributionCount: number;
  layoutRails: number;
  layoutLengthMeters: number;
  floorplanRails: number;
  floorplanLengthMeters: number;
};

export function railCountForSegment(lengthMeters: number, stockLengthMeters: number): number {
  if (!Number.isFinite(lengthMeters) || lengthMeters <= 0) return 0;
  const stock =
    Number.isFinite(stockLengthMeters) && stockLengthMeters > 0
      ? stockLengthMeters
      : DEFAULT_RAIL_STOCK_LENGTH_METERS;
  return Math.ceil(lengthMeters / stock);
}

export function polylineWorldLength(points: number[]): number {
  if (!Array.isArray(points) || points.length < 4) return 0;
  let total = 0;
  for (let i = 0; i + 3 < points.length; i += 2) {
    const dx = points[i + 2] - points[i];
    const dy = points[i + 3] - points[i + 1];
    total += Math.hypot(dx, dy);
  }
  return Number.isFinite(total) ? total : 0;
}

export function layoutTrackWorldLength(element: LayoutElement): number {
  if (!ALL_RAIL_TYPES.has(element.type)) return 0;

  const railType = resolveRailType(element);
  if (isJunctionRailType(railType)) return 0;

  if (isCurveRailType(railType)) {
    const radius = getCurveRadius(element.dimensions.width, element.dimensions.height);
    if (radius <= 0) return 0;
    const sweepDeg = getCurveSweepAngle(railType);
    return (2 * Math.PI * radius * sweepDeg) / 360;
  }

  if (isConnectorRailType(railType)) {
    return 0;
  }

  const ends = getRailEndpoints(element);
  if (ends.length >= 2) {
    return Math.hypot(ends[1].x - ends[0].x, ends[1].y - ends[0].y);
  }

  return Math.max(element.dimensions.width, 0);
}

export function layoutTrackLengthMeters(
  element: LayoutElement,
  scaleFactor?: number,
  dxfUnitsPerMeter?: number,
  calibration?: LayoutBomCalibrationInput
): number {
  if (!ALL_RAIL_TYPES.has(element.type)) return 0;

  const cal: LayoutBomCalibrationInput = {
    ...calibration,
    scaleFactor: calibration?.scaleFactor ?? scaleFactor,
    dxfUnitsPerMeter: calibration?.dxfUnitsPerMeter ?? dxfUnitsPerMeter,
  };

  const railType = resolveRailType(element);
  if (isJunctionRailType(railType) || isConnectorRailType(railType)) return 0;

  if (isCurveRailType(railType)) {
    return layoutCurveTrackLengthMeters(element, cal);
  }

  return layoutStraightTrackLengthBreakdown(element, cal).meters;
}

export function isLineLikeFloorplanEntity(meta?: DxfPolylineMeta): boolean {
  const type = (meta?.type || '').toUpperCase();
  return LINE_LIKE_DXF_TYPES.has(type);
}

export function buildRailBomSummary(params: {
  elements: Record<string, LayoutElement>;
  polylines?: number[][];
  polylineMeta?: DxfPolylineMeta[];
  scaleFactor?: number;
  dxfUnitsPerMeter?: number;
  stockLengthMeters?: number;
}): RailBomSummary {
  const stockLengthMeters =
    Number.isFinite(params.stockLengthMeters) && (params.stockLengthMeters as number) > 0
      ? (params.stockLengthMeters as number)
      : DEFAULT_RAIL_STOCK_LENGTH_METERS;

  let layoutLengthMeters = 0;
  let layoutRails = 0;
  let floorplanLengthMeters = 0;
  let contributionCount = 0;

  const polylines = params.polylines ?? [];
  const layoutCalibration: LayoutBomCalibrationInput = {
    floorplanBounds: polylines.length > 0,
    floorplanPolylineCount: polylines.length,
    scaleFactor: params.scaleFactor,
    dxfUnitsPerMeter: params.dxfUnitsPerMeter,
  };

  for (const element of Object.values(params.elements)) {
    const meters = layoutTrackLengthMeters(
      element,
      params.scaleFactor,
      params.dxfUnitsPerMeter,
      layoutCalibration
    );
    if (!(meters > 0)) continue;
    layoutLengthMeters += meters;
    layoutRails += railCountForSegment(meters, stockLengthMeters);
    contributionCount += 1;
  }

  const meta = params.polylineMeta ?? [];
  for (let i = 0; i < polylines.length; i++) {
    if (!isLineLikeFloorplanEntity(meta[i])) continue;
    const worldLen = polylineWorldLength(polylines[i]);
    if (worldLen <= 0) continue;
    const meters = worldUnitsToEffectiveMeters(
      worldLen,
      params.scaleFactor,
      params.dxfUnitsPerMeter
    );
    if (!(meters > 0)) continue;
    floorplanLengthMeters += meters;
    contributionCount += 1;
  }

  // Existing/imported floor-plan geometry is aggregated first, then quantized
  // once to avoid inflating counts from highly segmented DXF entities.
  const floorplanRails = railCountForSegment(floorplanLengthMeters, stockLengthMeters);

  return {
    stockLengthMeters,
    totalRails: layoutRails + floorplanRails,
    totalLengthMeters: layoutLengthMeters + floorplanLengthMeters,
    contributionCount,
    layoutRails,
    layoutLengthMeters,
    floorplanRails,
    floorplanLengthMeters,
  };
}
